import Slider from '@mui/material/Slider'
import { useMemo, useState } from 'react'

import style from './RobotControl.module.css'

type MotorRpmSliderProps = {
  label: string
  min: number
  max: number
  step: number
  telemetryRpm?: number | null
  disabled?: boolean
  onCommit: (rpm: number) => void | Promise<void>
}

const clampRpm = (value: number, min: number, max: number) => Math.min(max, Math.max(min, Math.round(value)))

const toPercent = (value: number, min: number, max: number) => {
  if (max <= min) {
    return 0
  }
  return ((clampRpm(value, min, max) - min) / (max - min)) * 100
}

const MotorRpmSlider = ({ label, min, max, step, telemetryRpm, disabled = false, onCommit }: MotorRpmSliderProps) => {
  const [committedTarget, setCommittedTarget] = useState<number | null>(null)
  const [draftValue, setDraftValue] = useState(min)
  const [isDragging, setIsDragging] = useState(false)

  const feedbackValue = useMemo(() => {
    if (telemetryRpm == null || !Number.isFinite(telemetryRpm)) {
      return null
    }
    return clampRpm(telemetryRpm, min, max)
  }, [telemetryRpm, min, max])

  const feedbackPercent = feedbackValue != null ? toPercent(feedbackValue, min, max) : 0
  const sliderValue = isDragging ? draftValue : committedTarget ?? min
  const showTargetThumb = committedTarget != null || isDragging

  return (
    <div className={style.rpmRow}>
      <div className={style.rpmRowHeader}>
        <span>{label}</span>
      </div>
      <div className={committedTarget != null ? style.rpmValues : style.rpmValuesFeedbackOnly}>
        {committedTarget != null && (
          <span className={style.rpmTargetValue}>
            <span className={style.rpmValueDotTarget} aria-hidden />
            Target <strong>{committedTarget}</strong> rpm
          </span>
        )}
        <span className={style.rpmFeedbackValue}>
          <span className={style.rpmValueDotFeedback} aria-hidden />
          Feedback <strong>{feedbackValue != null ? feedbackValue : '—'}</strong>
          {feedbackValue != null ? ' rpm' : ''}
        </span>
      </div>
      <div className={style.rpmSliderWrap}>
        <div className={style.rpmTrack} aria-hidden>
          <div className={style.rpmFeedbackFill} style={{ width: `${feedbackPercent}%` }} />
        </div>
        <Slider
          className={style.rpmSliderControl}
          min={min}
          max={max}
          step={step}
          value={sliderValue}
          disabled={disabled}
          onChange={(_, nextValue) => {
            setIsDragging(true)
            setDraftValue(clampRpm(nextValue as number, min, max))
          }}
          onChangeCommitted={(_, nextValue) => {
            const rpm = clampRpm(nextValue as number, min, max)
            setIsDragging(false)
            setCommittedTarget(rpm)
            setDraftValue(rpm)
            void onCommit(rpm)
          }}
          valueLabelDisplay="off"
          size="small"
          sx={{
            color: '#22c55e',
            '& .MuiSlider-rail': {
              opacity: 0,
              backgroundColor: 'transparent',
            },
            '& .MuiSlider-track': {
              opacity: 0,
              border: 'none',
              backgroundColor: 'transparent',
            },
            '& .MuiSlider-thumb': {
              width: 16,
              height: 16,
              backgroundColor: '#22c55e',
              border: '2px solid #fff',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.28)',
              opacity: showTargetThumb ? 1 : 0,
              transition: 'opacity 0.15s ease',
              '&:hover, &.Mui-focusVisible': {
                boxShadow: showTargetThumb ? '0 0 0 8px rgba(34, 197, 94, 0.18)' : 'none',
              },
            },
            '&.Mui-disabled .MuiSlider-thumb': {
              backgroundColor: '#9ca3af',
              borderColor: '#f3f4f6',
            },
          }}
        />
      </div>
      <div className={style.rpmBounds}>
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  )
}

export default MotorRpmSlider
